import React, { useState } from 'react';
import { BULK_ACTION_ERROR_MESSAGE } from '..';
import { AlertObject, AlertType } from '../../../../../../../components/Alert';
import { BulkActionButton } from '../BulkActionButton';
import { User } from '../../../../../../../entities/User';
import {
  checkIfAnySelectedCandidatesAreRedacted,
  checkIfSelectedCandidatesHaveSameStatus,
} from '../utils';
import { SendEmailModal } from '../../../../../../SendEmailModal';
import { ApplicationService } from '../../../../../../../services/applicant_tracking/ApplicationService';
import { SendEmailDto } from '../../../../../../../services/dtos/EmailDto';
import { ApiError } from '../../../../../../../services/ApiService/errors/ApiError';
import { NewListCandidate } from '../../../../../../../entities/v1/applicant_tracking/NewListCandidate';
import { getLabelForCount } from '../../../../../../../utils/grammar';
import { useBulkAction } from '../../../../../../../hooks/useBulkAction';

interface PropTypes {
  candidates: NewListCandidate[];
  currentUser: User;
  selectedCandidates: NewListCandidate[];
  bulkSelectedIds: number[] | null;
  selectionCountLabel: string;
  reloadPage: () => void;
  setAlert: (alert: AlertObject) => void;
}

export function SendEmailButton(props: PropTypes) {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState<boolean>(false);
  const { progress, execute } = useBulkAction();

  const actionDisabled =
    props.bulkSelectedIds !== null
      ? props.bulkSelectedIds.length === 0
      : props.selectedCandidates.length === 0 ||
        !checkIfSelectedCandidatesHaveSameStatus(props.selectedCandidates) ||
        checkIfAnySelectedCandidatesAreRedacted(props.selectedCandidates);
  const toolTipText = actionDisabled
    ? 'Bulk e-mail sending is only available for candidates that share the same status and are not-redacted'
    : 'Send Email';

  const applicationIds =
    props.bulkSelectedIds !== null
      ? props.bulkSelectedIds
      : props.selectedCandidates.map((c) => c.application!.id);

  const onSendEmail = async (sendEmailProps: SendEmailDto) => {
    if (props.bulkSelectedIds !== null) {
      const failedCount = await execute(applicationIds, (batchIds) =>
        ApplicationService.sendEmails(batchIds, sendEmailProps),
      );
      setIsEmailModalOpen(false);
      props.reloadPage();
      props.setAlert(
        failedCount > 0
          ? {
              message:
                'Some emails could not be submitted. Please try again for the affected applications.',
              type: AlertType.Danger,
            }
          : {
              message: (
                <span>
                  The email was successfully sent to{' '}
                  <span className='fw-bold'>{props.selectionCountLabel}</span>
                </span>
              ),
              type: AlertType.Success,
            },
      );
      return;
    }

    await ApplicationService.sendEmails(applicationIds, sendEmailProps)
      .then(() => {
        props.reloadPage();
        props.setAlert({
          message: (
            <span>
              The email was successfully sent to{' '}
              <span className='fw-bold'>{props.selectionCountLabel}</span>
            </span>
          ),
          type: AlertType.Success,
        });
      })
      .catch((e: ApiError) => {
        props.setAlert({
          message: e.body?.message || BULK_ACTION_ERROR_MESSAGE,
          type: AlertType.Danger,
        });
      })
      .finally(() => setIsEmailModalOpen(false));
  };

  return (
    <>
      <BulkActionButton
        iconName='bi-envelope'
        toolTipText={toolTipText}
        disabled={actionDisabled}
        onClick={() => setIsEmailModalOpen(true)}
        analyticsId='bulk-send-email-button'
      />
      {isEmailModalOpen && (
        <SendEmailModal
          applicationIds={applicationIds}
          toEmail={
            props.bulkSelectedIds === null &&
            props.selectedCandidates.length == 1 &&
            props.selectedCandidates[0].email
          }
          hasEmailConnected={props.currentUser.hasEmailConnected}
          isOpen={isEmailModalOpen}
          progress={progress}
          warningDescription={
            props.bulkSelectedIds !== null
              ? `This email will be sent to all selected applications regardless of their current status. Redacted applications will be skipped. Emails will be sent individually to ${getLabelForCount(
                  'candidate',
                  props.bulkSelectedIds.length,
                )}.`
              : undefined
          }
          onCloseModal={() => setIsEmailModalOpen(false)}
          onSendEmail={onSendEmail}
        />
      )}
    </>
  );
}
